import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  useTheme,
  alpha,
  Stack,
  Divider,
  IconButton,
  Fade,
  Link,
  Grid
} from '@mui/material';
import {
  Lock,
  Email,
  ArrowBack,
  Visibility,
  VisibilityOff,
  Business,
  TrendingUp,
  Security,
  Analytics
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { LoginFormData } from '../types';
import { API_CONFIG } from '../../../lib/config/api';
import { api } from '@services';
import axios, { AxiosError } from 'axios';
import { Button } from '@shared/components/ui/Button';
import designSystem from '@shared/utils/designSystem';
import {
  getGlassmorphismBackground,
  getPageBackgroundGradient,
  getBackgroundPattern,
  getBackButtonStyles,
  getTextFieldStyles
} from '../styles/authStyles';

// Type for API error responses
interface ApiErrorResponse {
  error?: string;
  message?: string;
  status?: string;
  accountStatus?: string;
}

// Type guard for checking if error has response
function isAxiosErrorWithResponse(error: unknown): error is AxiosError<ApiErrorResponse> {
  return axios.isAxiosError(error) && error.response !== undefined;
}

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const theme = useTheme();
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [isLoginComplete, setIsLoginComplete] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [, setApiInfo] = useState<{url?: string, status?: string}>({});

  // Check API connection on component mount for local development
  useEffect(() => {
    const checkApiConnection = async () => {
      try {
        const baseUrl = API_CONFIG.BASE_URL;
        setApiInfo(prev => ({ ...prev, url: baseUrl }));

        if (process.env.NODE_ENV === 'development') {
          setApiInfo(prev => ({ ...prev, status: 'checking...' }));
          try {
            await fetch(baseUrl, {
              method: 'HEAD',
              mode: 'no-cors',
            });
            setApiInfo(prev => ({ ...prev, status: 'connected' }));
          } catch {
            setApiInfo(prev => ({ ...prev, status: 'unreachable' }));
          }
        }
      } catch {
      // Error handled
    }
    };

    checkApiConnection();
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    const formData: LoginFormData = {
      email: event.currentTarget.email.value,
      password: event.currentTarget.password.value,
    };

    try {
      // Development mode logging removed

      interface LoginResponse {
        token?: string;
        user?: unknown;
      }
      const response = await api.post('/auth/login', formData);
      const data = response.data as LoginResponse;

      if (data?.token) {
        localStorage.setItem('token', data.token);

        if (data?.user) {
          localStorage.setItem('user', JSON.stringify(data.user));
        }

        setIsLoginComplete(true);

        try {
          await login(formData.email, formData.password);
          navigate('/dashboard', { replace: true });
        } catch {
          navigate('/dashboard', { replace: true });
        }
      } else {
        throw new Error('No token received from server');
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : '';

      // Handle account status errors (403)
      if (isAxiosErrorWithResponse(error) && error.response?.status === 403) {
        const data = error.response.data;

        if (data.status === 'pending' || data.accountStatus === 'pending') {
          setError('Your account is pending admin approval. Please check your email for updates.');
        } else if (data.status === 'rejected' || data.accountStatus === 'rejected') {
          setError('Your signup request was not approved. Please contact support for more information.');
        } else if (data.status === 'suspended' || data.accountStatus === 'suspended') {
          setError('Your account has been suspended. Please contact support.');
        } else {
          setError(data.error || 'Account not active. Please contact support.');
        }
      } else if (errorMessage === 'Network Error') {
        setError('Unable to connect to the server. Please check your network connection and make sure the backend is running.');
      } else if (isAxiosErrorWithResponse(error) && error.response?.status === 429) {
        setError('Too many login attempts. Please wait a few minutes before trying again.');
      } else if (isAxiosErrorWithResponse(error) && error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError('Login failed. Please check your credentials.');
      }

      setIsLoginComplete(false);
    } finally {
      setLoading(false);
    }
  };


  if (isLoginComplete) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{
          background: getPageBackgroundGradient(theme),
        }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress size={48} sx={{ color: theme.palette.primary.main }} />
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              fontFamily: designSystem.typography.fontFamily.base,
              fontWeight: designSystem.typography.fontWeight.medium,
            }}
          >
            Signing you in...
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: getPageBackgroundGradient(theme),
        position: 'relative',
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: theme.palette.mode === 'light' ? 0.4 : 0.3,
          background: getBackgroundPattern(theme),
        }}
      />

      {/* Back to Landing Button */}
      <IconButton
        onClick={() => navigate('/')}
        sx={getBackButtonStyles(theme)}
      >
        <ArrowBack />
      </IconButton>

      <Grid container sx={{ minHeight: '100vh' }}>
        {/* Left Side - Branding */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Container maxWidth="sm">
            <Box sx={{ textAlign: 'center', px: 4 }}>
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: designSystem.spacing.xl / 8,
                }}
              >
                <img
                  src={theme.palette.mode === 'dark' ? '/logo512-dark.png' : '/logo512.png'}
                  alt="Valuate Hotels"
                  style={{ width: 120, height: 120 }}
                />
              </Box>

              <Typography
                variant="h2"
                sx={{
                  fontWeight: designSystem.typography.fontWeight.bold,
                  mb: designSystem.spacing.lg / 8,
                  background: `linear-gradient(120deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  fontFamily: designSystem.typography.fontFamily.base,
                  letterSpacing: '-0.02em',
                }}
              >
                Valuate
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  color: theme.palette.text.secondary,
                  mb: designSystem.spacing['2xl'] / 8,
                  fontWeight: designSystem.typography.fontWeight.medium,
                  lineHeight: designSystem.typography.lineHeight.snug,
                  fontFamily: designSystem.typography.fontFamily.base,
                }}
              >
                Institutional Intelligence for Hotel Investment
              </Typography>

              <Stack spacing={3} sx={{ display: 'inline-flex', mx: 'auto' }}>
                {[
                  { icon: TrendingUp, text: 'Advanced Market Analytics' },
                  { icon: Analytics, text: 'Real-time Property Insights' },
                  { icon: Security, text: 'Enterprise-grade Security' },
                ].map((feature, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: `${designSystem.borderRadius.xs}px`,
                        backgroundColor: alpha(theme.palette.primary.main, 0.15),
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <feature.icon sx={{ fontSize: 20, color: theme.palette.primary.light }} />
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: designSystem.typography.fontWeight.medium,
                        color: theme.palette.text.secondary,
                        fontFamily: designSystem.typography.fontFamily.base,
                      }}
                    >
                      {feature.text}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Container>
        </Grid>

        {/* Right Side - Login Form */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 1,
          }}
        >
    <Container maxWidth="sm">
            <Fade appear={false} in timeout={800}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: `${designSystem.borderRadius.md}px`,
                  ...getGlassmorphismBackground(theme),
                  boxShadow: designSystem.shadows.xl,
                  overflow: 'hidden',
                  mx: { xs: 2, sm: 4 },
                  my: { xs: 4, md: 0 },
                }}
              >
                <CardContent sx={{ p: { xs: 4, md: 6 } }}>
                  {/* Mobile Logo */}
                  <Box sx={{ display: { xs: 'block', md: 'none' }, textAlign: 'center', mb: 4 }}>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: `${designSystem.borderRadius.sm}px`,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2,
                        boxShadow: designSystem.shadows.primaryMd,
                      }}
                    >
                      <Business sx={{ fontSize: 32, color: '#ffffff' }} />
                    </Box>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: designSystem.typography.fontWeight.bold,
                        background: `linear-gradient(120deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontFamily: designSystem.typography.fontFamily.base,
                      }}
                    >
                      Valuate
                    </Typography>
                  </Box>

                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: designSystem.typography.fontWeight.bold,
                      mb: designSystem.spacing.sm / 8,
                      color: theme.palette.text.primary,
                      textAlign: { xs: 'center', md: 'left' },
                      fontSize: { xs: '1.75rem', md: '2.25rem' },
                      fontFamily: designSystem.typography.fontFamily.base,
                      letterSpacing: '-0.02em',
                    }}
                  >
                    Welcome back
        </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: theme.palette.text.secondary,
                      mb: designSystem.spacing.xl / 8,
                      textAlign: { xs: 'center', md: 'left' },
                      fontSize: designSystem.typography.fontSize.base,
                      fontWeight: designSystem.typography.fontWeight.medium,
                      fontFamily: designSystem.typography.fontFamily.base,
                    }}
                  >
                    Sign in to access your institutional dashboard
        </Typography>

                  {error && (
                    <Alert
                      severity="error"
                      sx={{
                        mb: designSystem.spacing.lg / 8,
                        borderRadius: `${designSystem.borderRadius.sm}px`,
                        border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                        backgroundColor: alpha(theme.palette.error.main, 0.15),
                        color: theme.palette.mode === 'light'
                          ? theme.palette.error.dark
                          : theme.palette.error.light,
                        '& .MuiAlert-message': {
                          fontWeight: designSystem.typography.fontWeight.medium,
                          fontFamily: designSystem.typography.fontFamily.base,
                        },
                        '& .MuiAlert-icon': {
                          color: theme.palette.mode === 'light'
                            ? theme.palette.error.dark
                            : theme.palette.error.light,
                        },
                      }}
                    >
                      {error}
                    </Alert>
                  )}

                  <Box component="form" onSubmit={handleSubmit}>
                    <Stack spacing={3}>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            mb: designSystem.spacing.xs / 8,
                            color: theme.palette.text.primary,
                            fontWeight: designSystem.typography.fontWeight.semibold,
                            fontSize: designSystem.typography.fontSize.sm,
                            fontFamily: designSystem.typography.fontFamily.base,
                          }}
                        >
                          Email address
                        </Typography>
                        <TextField
                          fullWidth
                          id="email"
                          name="email"
                          type="email"
                          required
                          autoComplete="email"
                          autoFocus
                          disabled={loading}
                          placeholder="you@company.com"
                          InputProps={{
                            startAdornment: (
                              <Email sx={{ color: theme.palette.text.secondary, mr: 1.5, ml: 0.5, fontSize: 20 }} />
                            ),
                          }}
                          sx={getTextFieldStyles(theme)}
                        />
                      </Box>

                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            mb: designSystem.spacing.xs / 8,
                            color: theme.palette.text.primary,
                            fontWeight: designSystem.typography.fontWeight.semibold,
                            fontSize: designSystem.typography.fontSize.sm,
                            fontFamily: designSystem.typography.fontFamily.base,
                          }}
                        >
                          Password
                        </Typography>
                        <TextField
                          fullWidth
                          id="password"
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          required
                          autoComplete="current-password"
                          disabled={loading}
                          placeholder="Enter your password"
                          InputProps={{
                            startAdornment: (
                              <Lock sx={{ color: theme.palette.text.secondary, mr: 1.5, ml: 0.5, fontSize: 20 }} />
                            ),
                            endAdornment: (
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                                size="small"
                                sx={{
                                  mr: 0.5,
                                  color: theme.palette.text.secondary,
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                    color: theme.palette.primary.main,
                                  },
                                }}
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            ),
                          }}
                          sx={getTextFieldStyles(theme)}
                        />
                      </Box>

                      <Box sx={{ pt: 1 }}>
                        <Button
                          type="submit"
                          fullWidth
                          hierarchy="primary"
                          size="large"
                          disabled={loading}
                          sx={{
                            py: designSystem.spacing.sm / 8 + 0.75,
                            fontSize: designSystem.typography.fontSize.base,
                            fontFamily: designSystem.typography.fontFamily.base,
                          }}
                        >
                          {loading ? (
                            <Stack direction="row" spacing={2} alignItems="center">
                              <CircularProgress size={20} color="inherit" />
                              <Typography
                                sx={{
                                  fontFamily: designSystem.typography.fontFamily.base,
                                  fontWeight: designSystem.typography.fontWeight.semibold,
                                }}
                              >
                                Signing in...
                              </Typography>
                            </Stack>
                          ) : (
                            'Sign in'
                          )}
                        </Button>
                      </Box>
                    </Stack>
                  </Box>

                  <Divider sx={{ my: designSystem.spacing.xl / 8 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        px: designSystem.spacing.md / 8,
                        fontWeight: designSystem.typography.fontWeight.semibold,
                        color: theme.palette.text.secondary,
                        fontFamily: designSystem.typography.fontFamily.base,
                      }}
                    >
                      or
                    </Typography>
                  </Divider>

                  <Button
                    fullWidth
                    hierarchy="secondary"
                    size="large"
                    onClick={() => navigate('/signup')}
                    sx={{
                      py: designSystem.spacing.sm / 8 + 0.75,
                      fontSize: designSystem.typography.fontSize.base,
                      fontFamily: designSystem.typography.fontFamily.base,
                    }}
                  >
                    Create new account
                  </Button>

                  {/* Forgot Password Link */}
                  <Box sx={{ textAlign: 'center', mt: designSystem.spacing.lg / 8 }}>
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={() => navigate('/forgot-password')}
                      sx={{
                        color: theme.palette.text.secondary,
                        fontWeight: designSystem.typography.fontWeight.medium,
                        textDecoration: 'none',
                        fontFamily: designSystem.typography.fontFamily.base,
                        '&:hover': {
                          color: theme.palette.primary.main,
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      Forgot your password?
                    </Link>
                  </Box>

                </CardContent>
              </Card>
            </Fade>
          </Container>
        </Grid>
      </Grid>
      </Box>
  );
};

export default LoginPage;
